import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import { useAuth, homePathFor } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import logger from "../lib/logger";

export function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = await login(email, password);
      nav(loc.state?.from || homePathFor(user), { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md card-heritage">
          <h1 className="font-display text-4xl text-ink mb-2">Welcome back</h1>
          <p className="text-ink-soft mb-6">Sign in to continue your heritage journey.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="field-label" htmlFor="email">Email</label>
              <input id="email" type="email" required autoFocus className="input-heritage" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <label className="field-label" htmlFor="password">Password</label>
              <input id="password" type="password" required className="input-heritage" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <div className="mt-6 text-sm text-ink-soft flex justify-between">
            <Link to="/forgot-password" className="hover:text-terracotta">Forgot password?</Link>
            <Link to="/register" className="hover:text-terracotta" data-testid="login-to-register">Create account</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "",
                                     city: "", country: "", jamatkhana: "", phone: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = await register({
        email: form.email, password: form.password,
        first_name: form.first_name, last_name: form.last_name,
        profile: { city: form.city, country: form.country, jamatkhana: form.jamatkhana, phone: form.phone },
      });
      nav(homePathFor(user), { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-lg card-heritage">
          <h1 className="font-display text-4xl text-ink mb-2">Join the Community</h1>
          <p className="text-ink-soft mb-6">Create an account to share and track your heritage submissions.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">First Name *</label>
                <input required className="input-heritage" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="register-first-name" />
              </div>
              <div>
                <label className="field-label">Last Name *</label>
                <input required className="input-heritage" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="register-last-name" />
              </div>
            </div>
            <div>
              <label className="field-label">Email *</label>
              <input type="email" required className="input-heritage" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
            </div>
            <div>
              <label className="field-label">Password *</label>
              <input type="password" required minLength={6} className="input-heritage" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" />
              <div className="text-xs text-muted-h mt-1">At least 6 characters</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">City</label>
                <input className="input-heritage" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="register-city" />
              </div>
              <div>
                <label className="field-label">Country</label>
                <input className="input-heritage" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="register-country" />
              </div>
            </div>
            <div>
              <label className="field-label">Jamatkhana / Community</label>
              <input className="input-heritage" value={form.jamatkhana} onChange={(e) => set("jamatkhana", e.target.value)} data-testid="register-jamatkhana" />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="register-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="register-submit">
              {busy ? "Creating account…" : "Create Account"}
            </button>
          </form>
          <div className="mt-6 text-sm text-ink-soft text-center">
            Already a member? <Link to="/login" className="text-terracotta hover:underline" data-testid="register-to-login">Sign in</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
      try { const API = (await import("../lib/api")).default; await API.post("/auth/forgot-password", { email }); }
    catch (e) { logger.error("Forgot password call failed:", e); }
    setSent(true);
  };
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md card-heritage">
          <h1 className="font-display text-3xl text-ink mb-2">Reset Password</h1>
          {sent ? (
            <p className="text-ink-soft">If an account exists for <strong>{email}</strong>, a reset link has been sent.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4 mt-4">
              <input type="email" required placeholder="Your email" className="input-heritage" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
              <button className="btn-primary w-full justify-center" data-testid="forgot-submit">Send Reset Link</button>
            </form>
          )}
          <div className="mt-4"><Link to="/login" className="text-sm text-terracotta hover:underline">Back to sign in</Link></div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function VerifyEmail() {
  const params = new URLSearchParams(window.location.search);
  const [status, setStatus] = useState("verifying");
  useState(() => {
    (async () => {
      try {
        const API = (await import("../lib/api")).default;
        await API.post("/auth/verify-email", { token: params.get("token") });
        setStatus("ok");
      } catch { setStatus("fail"); }
    })();
  }, []);
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="card-heritage max-w-md text-center">
          <h1 className="font-display text-3xl text-ink mb-3">Email Verification</h1>
          {status === "verifying" && <p className="text-ink-soft">Verifying…</p>}
          {status === "ok" && <p className="text-ink-soft">✓ Your email has been verified. You can now use all account features.</p>}
          {status === "fail" && <p className="text-red-700">Verification link is invalid or expired.</p>}
          <Link to="/" className="btn-outline mt-6 inline-flex">Back to Home</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
